import React, { useEffect, useMemo, useRef, useState } from 'react';
import { skills } from '../../Data';
import {
  SiCss3,
  SiFramer,
  SiHtml5,
  SiJavascript,
  SiNextdotjs,
  SiReact,
  SiReacthookform,
  SiRedux,
  SiTailwindcss,
  SiTypescript,
} from 'react-icons/si';

const ICON_BY_SKILL = {
  JavaScript: SiJavascript,
  TypeScript: SiTypescript,
  'React.js': SiReact,
  React: SiReact,
  'Next.js': SiNextdotjs,
  HTML5: SiHtml5,
  CSS3: SiCss3,
  'Tailwind CSS': SiTailwindcss,
  'Material UI': SiReact,
  Redux: SiRedux,
  Zustand: SiReact,
  'Framer Motion': SiFramer,
  'React Hook Form': SiReacthookform,
};

function levelFromPercentage(pct) {
  if (pct >= 92) return { label: 'Expert', tone: 'bg-accent/10 text-accent border-accent/20' };
  if (pct >= 85) return { label: 'Advanced', tone: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' };
  return { label: 'Proficient', tone: 'bg-white/5 text-white/70 border-white/10' };
}

const Skills = () => {
  const rootRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const items = useMemo(
    () =>
      skills.map((s) => {
        const Icon = ICON_BY_SKILL[s.name] || SiReact;
        return { ...s, Icon, level: levelFromPercentage(s.percentage) };
      }),
    []
  );

  return (
    <section
      id="skills"
      ref={rootRef}
      className="relative overflow-hidden bg-gradient-to-b from-[#070a10] to-[#0b0f17] py-20"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-gradient-to-tr from-accent/18 via-emerald-500/10 to-transparent blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-6 relative">
        <div
          className={[
            'text-center mb-12 md:mb-16 transition-all duration-700',
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
          ].join(' ')}
        >
          <p className="text-xs tracking-[0.22em] uppercase text-accent/80">Skills</p>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold text-white tracking-tight">
            My Technical Skills
          </h2>
          <p className="mt-4 text-sm md:text-base text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Technologies I use to build scalable frontend applications.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
          {items.map(({ id, name, description, Icon, level }, idx) => (
            <article
              key={id || name}
              className={[
                'group rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6 text-center shadow-soft',
                'transition-transform duration-300 hover:scale-[1.05] hover:border-accent/50 hover:shadow-[0_20px_60px_rgba(34,197,94,0.10)]',
                'transition-all duration-700',
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
              ].join(' ')}
              style={{ transitionDelay: inView ? `${Math.min(idx, 10) * 40}ms` : '0ms' }}
            >
              <div className="space-y-2">
                <div className="h-10 w-10 mx-auto rounded-xl border border-white/10 bg-black/30 grid place-items-center text-accent">
                  <Icon className="h-5 w-5" />
                </div>

                <h3 className="text-lg font-semibold text-white">{name}</h3>

                <div
                  className={[
                    'inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-semibold border',
                    level.tone,
                  ].join(' ')}
                >
                  {level.label}
                </div>

                <p className="text-sm text-gray-400 leading-relaxed mt-2 line-clamp-3">
                  {description}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Skills